
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const button3dVariants = cva(
  "relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 transform-gpu active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary: [
          "bg-gradient-to-b from-primary to-primary/90 text-primary-foreground",
          "shadow-[0_6px_0_#be185d] hover:shadow-[0_4px_0_#be185d]",
          "active:shadow-[0_2px_0_#be185d] active:translate-y-1",
          "border border-primary/20"
        ],
        lightblue: [
          "bg-gradient-to-b from-blue-300 to-blue-400 text-white",
          "shadow-[0_6px_0_#60a5fa] hover:shadow-[0_4px_0_#60a5fa]",
          "active:shadow-[0_2px_0_#60a5fa] active:translate-y-1",
          "border border-blue-200/20"
        ],
        secondary: [
          "bg-gradient-to-b from-white to-gray-50 text-gray-900 border border-gray-200",
          "shadow-[0_4px_0_#e5e7eb] hover:shadow-[0_2px_0_#e5e7eb]",
          "active:shadow-[0_1px_0_#e5e7eb] active:translate-y-1"
        ],
        success: [
          "bg-gradient-to-b from-green-500 to-green-600 text-white",
          "shadow-[0_6px_0_#059669] hover:shadow-[0_4px_0_#059669]",
          "active:shadow-[0_2px_0_#059669] active:translate-y-1",
          "border border-green-400/20"
        ],
        danger: [
          "bg-gradient-to-b from-red-500 to-red-600 text-white",
          "shadow-[0_6px_0_#dc2626] hover:shadow-[0_4px_0_#dc2626]",
          "active:shadow-[0_2px_0_#dc2626] active:translate-y-1",
          "border border-red-400/20"
        ]
      },
      size: {
        default: "h-12 px-6 py-3 text-base",
        sm: "h-10 px-4 py-2 text-sm",
        lg: "h-14 px-8 py-4 text-lg",
        xl: "h-16 px-10 py-5 text-xl",
        icon: "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
)

export interface Button3DProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button3dVariants> {
  asChild?: boolean
}

const Button3D = React.forwardRef<HTMLButtonElement, Button3DProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(button3dVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button3D.displayName = "Button3D"

export { Button3D, button3dVariants }
